import React from 'react';
import Leaflet, {LatLngBoundsLiteral, LatLngLiteral, LeafletMouseEvent} from 'leaflet';
import {ImageOverlay, Marker, Polyline, useMap} from 'react-leaflet';
import {useController} from 'react-hook-form';
import {gql} from '@apollo/client';
import {useApolloClient, useQuery} from '@apollo/client/react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import {toast} from 'react-toastify';
import MapContainer from 'components/common/MapContainer';
import MapEventHandlers from 'components/common/MapEventHandlers';
import ImageFileUpload from 'components/Map/Image/ImageFileUpload';
import FilePicker from 'components/common/FilePicker';
import RangeInput from 'components/common/RangeInput';
import {TextInput, SwitchInput} from 'components/forms/Inputs';
import {convertGpxToTrackArray} from 'services/gpxUtils';
import QUERIES from 'services/gqlQueries';
import {georeferenceMapFromPins} from 'services/georeferenceMapFromPins';
import ImageOverlayRotated from 'components/MyEvents/Track/ImageOverlayRotated';

export type MapGeorefPin = {
  order: number;
  x: number;
  y: number;
};

export type SubEventMapEvent = {
  location?: LatLngLiteral;
  zoom?: number;
  slug?: string;
  startDate?: string;
  endDate?: string;
};

type GpsSource = 'gpx' | 'device' | 'activity';
type ActivityUser = {id: number; name?: string | null; firstName?: string | null; lastName?: string | null};

const USER_SEARCH_MIN_CHARS = 2;
const USER_SEARCH_LIMIT = 25;
const USER_SEARCH_DEBOUNCE_MS = 300;

const userLabel = (user: ActivityUser) =>
  user.name?.trim() || `${user.firstName || ''} ${user.lastName || ''}`.trim() || `User ${user.id}`;

const GPS_HISTORY_BATCH_LIMIT = 5000;
const GPS_HISTORY_MAX_BATCHES = 200;

const DEVICE_GPS_HISTORY = gql`
  query DeviceGpsHistory($deviceName: String!, $from: Date!, $to: Date!, $limit: Int, $cursor: String) {
    deviceGpsHistory(deviceName: $deviceName, from: $from, to: $to, limit: $limit, cursor: $cursor) {
      points {
        time
        lat
        lng
      }
      hasMore
      nextCursor
    }
  }
`;

const SEARCH_USERS = gql`
  query SearchUsersForActivity($filters: UserFilters, $size: Int) {
    items: allUsers(page: 1, size: $size, filters: $filters) {
      id
      name
      firstName
      lastName
    }
  }
`;

const GET_COMPETITOR_ACTIVITIES = gql`
  query GetCompetitorActivities($slug: String!, $userId: Int) {
    items: getCompetitorActivities(slug: $slug, userId: $userId) {
      id
      name
      provider
      points {
        lat
        lng
      }
    }
  }
`;

const pinIcon = (label: string, selected: boolean) =>
  Leaflet.divIcon({
    className: '',
    iconSize: [26, 26],
    iconAnchor: [13, 13],
    html: `<div style="width:26px;height:26px;border-radius:50%;background:${
      selected ? '#1565c0' : '#d32f2f'
    };color:#fff;font:700 13px/26px sans-serif;text-align:center;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4)">${label}</div>`
  });

const pinLabel = (order: number) => (order === 0 ? 'S' : String(order));

const InvalidateSize = () => {
  const map = useMap();
  React.useEffect(() => {
    const timer = window.setTimeout(() => map.invalidateSize(), 150);
    return () => window.clearTimeout(timer);
  }, [map]);
  return null;
};

const FitGeorefOnce = ({
  topLeft,
  topRight,
  bottomLeft
}: {
  topLeft: LatLngLiteral;
  topRight: LatLngLiteral;
  bottomLeft: LatLngLiteral;
}) => {
  const map = useMap();
  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      map.invalidateSize();
      map.fitBounds(
        [
          [topLeft.lat, topLeft.lng],
          [topRight.lat, topRight.lng],
          [bottomLeft.lat, bottomLeft.lng]
        ],
        {padding: [24, 24]}
      );
    }, 200);
    return () => window.clearTimeout(timer);
    // Fit once when the overlay first appears. Later pin and crop edits keep the current view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);
  return null;
};

const leafletToPixel = (latlng: LatLngLiteral, height: number) => ({
  x: latlng.lng,
  y: height - latlng.lat
});

const pixelToLeaflet = (pin: MapGeorefPin, height: number): LatLngLiteral => ({
  lat: height - pin.y,
  lng: pin.x
});

const applyLoadedTrack = (track: LatLngLiteral[], setSourceTrack: (track: LatLngLiteral[]) => void, setCropRange: (range: number[]) => void) => {
  setSourceTrack(track);
  setCropRange(track.length > 0 ? [0, track.length - 1] : [0, 0]);
};

const DeviceGpsPicker = ({event, onTrack}: {event?: SubEventMapEvent; onTrack: (track: LatLngLiteral[]) => void}) => {
  const client = useApolloClient();
  const {data, loading} = useQuery<{items: {id: number; name: string}[]}>(QUERIES.getDevices);
  const [selected, setSelected] = React.useState<{id: number; name: string} | null>(null);
  const [loadingTrack, setLoadingTrack] = React.useState(false);
  const devices = data?.items || [];

  const loadTrack = async (deviceName: string) => {
    if (!event?.startDate) {
      toast.error('Event start time is missing; cannot load a GPS device track');
      return;
    }
    setLoadingTrack(true);
    const from = new Date(event.startDate).getTime();
    const to = event.endDate ? new Date(event.endDate).getTime() : Date.now();
    try {
      type HistoryPage = {
        points: Array<{time: string | number; lat: number; lng: number}>;
        hasMore: boolean;
        nextCursor: string | null;
      };
      const rows: HistoryPage['points'] = [];
      let cursor: string | undefined;
      for (let batch = 0; batch < GPS_HISTORY_MAX_BATCHES; batch += 1) {
        const res = await client.query<{deviceGpsHistory: HistoryPage}>({
          query: DEVICE_GPS_HISTORY,
          variables: {
            deviceName,
            from,
            to,
            limit: GPS_HISTORY_BATCH_LIMIT,
            cursor: cursor ?? null
          },
          fetchPolicy: 'network-only',
          errorPolicy: 'all'
        });
        if (res.error) {
          toast.error(res.error.message);
          return;
        }
        const page = res.data?.deviceGpsHistory;
        if (!page?.points?.length) break;
        rows.push(...page.points);
        if (!page.hasMore || !page.nextCursor) break;
        cursor = page.nextCursor;
      }
      const track = rows
        .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng))
        .map((p) => ({lat: p.lat, lng: p.lng}));
      if (track.length === 0) {
        toast.error('No GPS points for this device in the event time frame');
        onTrack([]);
        return;
      }
      onTrack(track);
      toast.success(`GPS track loaded (${track.length} points). Place pins in visit order.`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to load GPS device track');
    } finally {
      setLoadingTrack(false);
    }
  };

  return (
    <Stack direction={{xs: 'column', sm: 'row'}} spacing={1} alignItems={{sm: 'center'}}>
      <Autocomplete
        size="small"
        sx={{minWidth: 240, flex: 1}}
        options={devices}
        loading={loading}
        value={selected}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={(_, value) => setSelected(value)}
        renderInput={(params) => <TextField {...params} label="GPS device" />}
      />
      <Button
        size="small"
        variant="contained"
        disabled={!selected || loadingTrack}
        onClick={() => selected && void loadTrack(selected.name)}
      >
        {loadingTrack ? 'Loading…' : 'Load device track'}
      </Button>
    </Stack>
  );
};

const ActivityGpsPicker = ({event, onTrack}: {event?: SubEventMapEvent; onTrack: (track: LatLngLiteral[]) => void}) => {
  const [selectedUser, setSelectedUser] = React.useState<ActivityUser | null>(null);
  const [inputValue, setInputValue] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [selectedId, setSelectedId] = React.useState<string>('');

  React.useEffect(() => {
    const timer = window.setTimeout(() => setSearch(inputValue.trim()), USER_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [inputValue]);

  const {data: usersData, loading: usersLoading} = useQuery<{items: ActivityUser[]}>(SEARCH_USERS, {
    variables: {filters: {search}, size: USER_SEARCH_LIMIT},
    skip: search.length < USER_SEARCH_MIN_CHARS,
    fetchPolicy: 'network-only'
  });

  const {data, loading} = useQuery<{
    items: {id: string; name: string; provider?: string; points: LatLngLiteral[]}[];
  }>(GET_COMPETITOR_ACTIVITIES, {
    variables: {slug: event?.slug || '', userId: selectedUser?.id},
    skip: !event?.slug || !selectedUser?.id,
    fetchPolicy: 'network-only'
  });

  const users = React.useMemo(() => {
    const items = usersData?.items || [];
    if (selectedUser && !items.some((user) => user.id === selectedUser.id)) {
      return [selectedUser, ...items];
    }
    return items;
  }, [usersData?.items, selectedUser]);
  const activities = data?.items || [];

  React.useEffect(() => {
    setSelectedId('');
  }, [selectedUser?.id]);

  if (!event?.slug) {
    return (
      <Typography variant="body2" color="text.secondary">
        Event slug is missing; cannot load watch activities.
      </Typography>
    );
  }

  const onSelectActivity = (id: string) => {
    setSelectedId(id);
    const activity = activities.find((item) => item.id === id);
    const track = (activity?.points || []).filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
    if (track.length === 0) {
      toast.error('This activity has no GPS points');
      onTrack([]);
      return;
    }
    onTrack(track);
    toast.success(`GPS track loaded (${track.length} points). Place pins in visit order.`);
  };

  return (
    <Stack spacing={1}>
      <Autocomplete
        size="small"
        sx={{minWidth: 240}}
        options={users}
        loading={usersLoading}
        value={selectedUser}
        inputValue={inputValue}
        filterOptions={(options) => options}
        getOptionLabel={userLabel}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={(_, value) => setSelectedUser(value)}
        onInputChange={(_, value) => setInputValue(value)}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search user"
            helperText={
              search.length > 0 && search.length < USER_SEARCH_MIN_CHARS
                ? `Type at least ${USER_SEARCH_MIN_CHARS} characters`
                : 'Activities are loaded for this user in the event time frame'
            }
          />
        )}
      />
      {!selectedUser ? (
        <Typography variant="body2" color="text.secondary">
          Search and select a user to load watch activities.
        </Typography>
      ) : loading ? (
        <CircularProgress size={22} />
      ) : activities.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No watch activities with GPS in the event time frame for this user.
        </Typography>
      ) : (
        <RadioGroup value={selectedId} onChange={(e) => onSelectActivity(e.target.value)}>
          {activities.map((item) => (
            <FormControlLabel
              key={item.id}
              value={item.id}
              control={<Radio size="small" />}
              label={item.provider ? `${item.provider}: ${item.name}` : item.name}
            />
          ))}
        </RadioGroup>
      )}
    </Stack>
  );
};

const SubEventMapField = ({
  event,
  presetTrack,
  maxPins
}: {
  event?: SubEventMapEvent;
  /** GPS track already chosen (for example a watch activity). Hides the GPX / device / activity pickers. */
  presetTrack?: LatLngLiteral[];
  maxPins?: number;
}) => {
  const [imageSrcData, setImageSrcData] = React.useState<string>();
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [gpsSource, setGpsSource] = React.useState<GpsSource>('gpx');
  const [sourceTrack, setSourceTrack] = React.useState<LatLngLiteral[]>([]);
  const [cropRange, setCropRange] = React.useState<number[]>([0, 0]);

  React.useEffect(() => {
    if (!presetTrack) return;
    applyLoadedTrack(presetTrack, setSourceTrack, setCropRange);
  }, [presetTrack]);

  const {
    field: {value: pins = [], onChange: setPins}
  } = useController({name: 'pins', defaultValue: [] as MapGeorefPin[]});

  const {
    field: {onChange: setGpsTrack}
  } = useController({name: 'gpsTrack', defaultValue: [] as LatLngLiteral[]});

  const {
    field: {value: height = 0}
  } = useController({name: 'height', defaultValue: 0});

  const {
    field: {value: width = 0}
  } = useController({name: 'width', defaultValue: 0});

  useController({name: 'createControls', defaultValue: true});

  const typedPins = pins as MapGeorefPin[];
  const selectedPin = typedPins[selectedIndex];
  const croppedTrack = React.useMemo(() => {
    if (sourceTrack.length === 0) return [];
    const start = cropRange[0] ?? 0;
    const end = cropRange[1] ?? sourceTrack.length - 1;
    return sourceTrack.slice(start, end + 1);
  }, [sourceTrack, cropRange]);

  React.useEffect(() => {
    setGpsTrack(croppedTrack);
  }, [croppedTrack, setGpsTrack]);

  const mapBounds: LatLngBoundsLiteral = React.useMemo(
    () => [
      [0, 0],
      [height, width]
    ],
    [height, width]
  );

  const handleMapClick = (e: LeafletMouseEvent) => {
    if (!height || !width) return;
    if (maxPins != null && typedPins.length >= maxPins) {
      toast.error(`Place only ${maxPins} pins`);
      return;
    }
    const pixel = leafletToPixel(e.latlng, height);
    const next: MapGeorefPin = {
      order: typedPins.length,
      x: pixel.x,
      y: pixel.y
    };
    setPins([...typedPins, next]);
    setSelectedIndex(typedPins.length);
  };

  const updatePin = (index: number, patch: Partial<MapGeorefPin>) => {
    setPins(typedPins.map((pin, i) => (i === index ? {...pin, ...patch} : pin)));
  };

  const removeSelectedPin = () => {
    if (!selectedPin) return;
    const next = typedPins.filter((_, i) => i !== selectedIndex).map((pin, i) => ({...pin, order: i}));
    setPins(next);
    setSelectedIndex(Math.max(0, Math.min(selectedIndex, next.length - 1)));
  };

  const applyTrack = (track: LatLngLiteral[]) => applyLoadedTrack(track, setSourceTrack, setCropRange);

  const handleGpxFileChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (extension !== 'gpx') {
      toast.error('The file is not in GPX format');
      return;
    }
    const reader = new window.FileReader();
    reader.onload = (event) => {
      const track = convertGpxToTrackArray(event.target?.result as string).map((point) => ({
        lat: point.lat,
        lng: point.lng
      }));
      applyTrack(track);
      toast.success(`GPS track loaded (${track.length} points). Place pins in visit order.`);
    };
    reader.readAsText(file);
  };

  const handleGpsSource = (_: React.MouseEvent<HTMLElement>, value: GpsSource | null) => {
    if (!value) return;
    setGpsSource(value);
  };

  const gpsCenter = croppedTrack[0] || event?.location;

  const overlayCorners = React.useMemo(() => {
    if (!imageSrcData || height <= 0 || width <= 0 || typedPins.length < 2 || croppedTrack.length < 2) return null;
    try {
      return georeferenceMapFromPins(width, height, typedPins, croppedTrack).corners;
    } catch {
      return null;
    }
  }, [imageSrcData, height, width, typedPins, croppedTrack]);

  const applyCrop = (value: number[]) => {
    setCropRange(value);
  };

  return (
    <Box sx={{mt: 1}}>
      <Typography variant="subtitle2" sx={{mb: 0.5}}>
        Map (optional)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
        {presetTrack
          ? 'You can create the event without a map. To add one, upload an image and place the start and end pins. The image is drawn on the track and moves when you drag those pins or crop the track.'
          : "Upload a map image and place numbered pins in the order those places were visited. Add a GPS track from a GPX file, a recorded GPS device, or a Garmin / Suunto / COROS activity in this event's time frame. Crop the track, then the image is drawn on that track and updates as you move the pins or crop."}
      </Typography>
      <ImageFileUpload onImageSrcChange={setImageSrcData} />
      {imageSrcData && (
        <React.Fragment>
          <TextInput fullWidth label="Map name" name="mapName" />
          <Typography variant="body2" sx={{mt: 1, mb: 0.5}}>
            {maxPins === 2
              ? 'Click the map to place the start pin, then the end pin, on points that appear on the GPS track.'
              : 'Click the map to add pins S, 1, 2… in visit order. The first pin is the start.'}
          </Typography>
          {height > 0 && width > 0 && (
            <MapContainer
              className="leaflet-crosshair"
              bounds={mapBounds}
              style={{width: '100%', height: 420, position: 'relative'}}
              crs={Leaflet.CRS.Simple}
              minZoom={-4}
              noTileLayer
            >
              <InvalidateSize />
              <MapEventHandlers click={handleMapClick} />
              <ImageOverlay url={imageSrcData} bounds={mapBounds} />
              {typedPins.map((pin, i) => (
                <Marker
                  key={pin.order}
                  position={pixelToLeaflet(pin, height)}
                  icon={pinIcon(pinLabel(pin.order), i === selectedIndex)}
                  draggable
                  eventHandlers={{
                    click: (e) => {
                      Leaflet.DomEvent.stopPropagation(e);
                      setSelectedIndex(i);
                    },
                    drag: (e) => {
                      const pixel = leafletToPixel(e.target.getLatLng(), height);
                      updatePin(i, pixel);
                    },
                    dragend: (e) => {
                      const pixel = leafletToPixel(e.target.getLatLng(), height);
                      updatePin(i, pixel);
                    }
                  }}
                />
              ))}
            </MapContainer>
          )}
          <Stack direction="row" spacing={1} alignItems="center" sx={{mt: 1}} flexWrap="wrap">
            <Typography variant="body2">
              {selectedPin ? `Selected pin ${pinLabel(selectedPin.order)}` : 'No pins yet'}
            </Typography>
            <Button size="small" disabled={!selectedPin} onClick={removeSelectedPin}>
              Remove pin
            </Button>
            <SwitchInput name="createControls" label="Create controls" sx={{width: 'auto'}} />
            <Typography variant="body2" color="text.secondary">
              {croppedTrack.length}
              {sourceTrack.length !== croppedTrack.length ? ` / ${sourceTrack.length}` : ''} GPS points
            </Typography>
          </Stack>
          {presetTrack ? null : (
            <React.Fragment>
              <ToggleButtonGroup exclusive size="small" value={gpsSource} onChange={handleGpsSource} sx={{mt: 1, mb: 1}}>
                <ToggleButton value="gpx">GPX file</ToggleButton>
                <ToggleButton value="device">GPS device</ToggleButton>
                <ToggleButton value="activity">Watch activity</ToggleButton>
              </ToggleButtonGroup>
              {(() => {
                switch (gpsSource) {
                  case 'gpx':
                    return <FilePicker label="Upload GPX file" accept=".gpx" onFileChange={handleGpxFileChange} />;
                  case 'device':
                    return <DeviceGpsPicker event={event} onTrack={applyTrack} />;
                  case 'activity':
                    return <ActivityGpsPicker event={event} onTrack={applyTrack} />;
                  default: {
                    const _exhaustive: never = gpsSource;
                    return _exhaustive;
                  }
                }
              })()}
            </React.Fragment>
          )}
          {gpsCenter && sourceTrack.length > 0 && (
            <React.Fragment>
              {sourceTrack.length > 1 && (
                <FormControl fullWidth margin="dense" sx={{mt: 1}}>
                  <Typography variant="body2" color="text.secondary">
                    Crop GPS track ({(cropRange[0] ?? 0) + 1}–{(cropRange[1] ?? 0) + 1} of {sourceTrack.length})
                  </Typography>
                  <RangeInput min={0} max={sourceTrack.length - 1} onChange={applyCrop} value={cropRange} />
                </FormControl>
              )}
              <MapContainer
                center={gpsCenter}
                zoom={event?.zoom || 14}
                style={{width: '100%', height: 420, position: 'relative', marginTop: 8}}
              >
                <InvalidateSize />
                {overlayCorners && (
                  <React.Fragment>
                    <FitGeorefOnce
                      topLeft={overlayCorners.topLeft}
                      topRight={overlayCorners.topRight}
                      bottomLeft={overlayCorners.bottomLeft}
                    />
                    <ImageOverlayRotated
                      image={imageSrcData}
                      topLeft={overlayCorners.topLeft}
                      topRight={overlayCorners.topRight}
                      bottomLeft={overlayCorners.bottomLeft}
                      opacity={0.75}
                    />
                  </React.Fragment>
                )}
                {(cropRange[0] > 0 || cropRange[1] < sourceTrack.length - 1) && (
                  <Polyline color="gray" opacity={0.35} positions={sourceTrack} />
                )}
                {croppedTrack.length > 0 && <Polyline color="green" opacity={0.9} positions={croppedTrack} />}
              </MapContainer>
              {typedPins.length >= 2 && !overlayCorners && (
                <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
                  Move the start and end pins farther apart to place the map on the track.
                </Typography>
              )}
            </React.Fragment>
          )}
        </React.Fragment>
      )}
    </Box>
  );
};

export default SubEventMapField;
